/**
 * Timeline — блок «История оплат»: вертикальный таймлайн событий (GET /events).
 * Цветная точка + иконка + текст (title из EventLog) + автор/время.
 *
 * Все 13 типов EventType имеют метку/тон/иконку. Отображаем серверный `title` (RU), а если его нет —
 * дефолтную метку типа. Автор — по actorAmoUserId (имя недоступно в DTO событий → показываем id/«Система»).
 */

import type { EventLogDto, EventType } from '../../lib/types';
import { fmtDateTime } from './lib/dates';
import { cx } from './ui/cx';
import {
  IconBan,
  IconCalendar,
  IconCheck,
  IconClock,
  IconMoney,
  IconPencil,
  IconPlus,
  IconTrash,
  IconWallet,
  IconWarn,
} from './ui/icons';

type EventTone = 'neutral' | 'info' | 'success' | 'danger' | 'warning' | 'muted';

interface EventMeta {
  tone: EventTone;
  label: string;
  icon: JSX.Element;
}

const EVENT_META: Record<EventType, EventMeta> = {
  PAYMENT_CREATED: { tone: 'neutral', label: 'Платёж создан', icon: <IconPlus size={13} /> },
  AMOUNT_CHANGED: { tone: 'info', label: 'Изменена сумма', icon: <IconMoney size={13} /> },
  DATE_CHANGED: { tone: 'info', label: 'Изменена дата', icon: <IconCalendar size={13} /> },
  PAYMENT_RECEIVED: { tone: 'success', label: 'Получена оплата', icon: <IconCheck size={13} /> },
  PARTIAL_PAYMENT: { tone: 'info', label: 'Частичная оплата', icon: <IconWallet size={13} /> },
  PAYMENT_OVERDUE: { tone: 'danger', label: 'Платёж просрочен', icon: <IconWarn size={13} /> },
  TASK_CREATED: { tone: 'warning', label: 'Создана задача', icon: <IconClock size={13} /> },
  TASK_CLOSED: { tone: 'success', label: 'Задача закрыта', icon: <IconCheck size={13} /> },
  STATUS_CHANGED: { tone: 'neutral', label: 'Изменён статус', icon: <IconPencil size={13} /> },
  PAYMENT_CANCELLED: { tone: 'muted', label: 'Платёж отменён', icon: <IconBan size={13} /> },
  PAYMENT_DELETED: { tone: 'muted', label: 'Платёж удалён', icon: <IconTrash size={13} /> },
  PLAN_RESCHEDULED: { tone: 'info', label: 'Перенос срока', icon: <IconClock size={13} /> },
  DEAL_BUDGET_CHANGED: { tone: 'info', label: 'Изменён бюджет', icon: <IconMoney size={13} /> },
};

export interface TimelineProps {
  events: EventLogDto[];
  isLoading?: boolean;
}

function actorLabel(actorAmoUserId: string | null): string {
  if (!actorAmoUserId) return 'Система';
  return `Пользователь ${actorAmoUserId}`;
}

export function Timeline({ events, isLoading }: TimelineProps): JSX.Element | null {
  if (isLoading) {
    return (
      <section className="pw-timeline" aria-label="История оплат">
        <h3 className="pw-timeline__heading">История оплат</h3>
        <div className="pw-timeline__loading" aria-busy="true">
          <span className="pw-spinner" aria-hidden />
        </div>
      </section>
    );
  }

  if (events.length === 0) return null;

  return (
    <section className="pw-timeline" aria-label="История оплат">
      <h3 className="pw-timeline__heading">История оплат</h3>
      <ol className="pw-timeline__list">
        {events.map((e) => {
          const meta = EVENT_META[e.type];
          return (
            <li key={e.id} className="pw-timeline__item">
              <span className={cx('pw-timeline__dot', `pw-timeline__dot--${meta.tone}`)} aria-hidden>
                {meta.icon}
              </span>
              <div className="pw-timeline__body">
                <div className="pw-timeline__row">
                  <strong className="pw-timeline__title">{e.title ?? meta.label}</strong>
                  <time className="pw-timeline__time">{fmtDateTime(e.occurredAt)}</time>
                </div>
                <span className="pw-timeline__actor">{actorLabel(e.actorAmoUserId)}</span>
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
